/**
 * Get the locale that is exact match in the available locales
 * @param languageCode the language code for the locale to look-up
 * @param countryCode the country code for the locale to look-up
 * @param availableLocales an object where the keys are locale codes and the values are locales
 * @returns {*}
 */
function getAvailableLocale(languageCode, countryCode, availableLocales) {
  /*
   * Check all possible combinations of the locale code in availableLocales
   * If country code is undefined then we need to only check for the language code
   */
  if (countryCode === undefined) {
    const languageUpperCase = `${languageCode}`;
    const languageLowerCase = `${languageCode}`;
    if (availableLocales[languageUpperCase]) {
      return availableLocales[languageUpperCase];
    } else if (availableLocales[languageLowerCase]) {
      return availableLocales[languageLowerCase];
    }
  } else {
    const localeHyphenUpperCase = `${languageCode}-${countryCode.toUpperCase()}`;
    const localeHyphenLowerCase = `${languageCode}-${countryCode.toLowerCase()}`;
    const localeUnderscoreUpperCase = `${languageCode}_${countryCode.toUpperCase()}`;
    const localeUnderscoreLowerCase = `${languageCode}_${countryCode.toLowerCase()}`;
    if (availableLocales[localeHyphenUpperCase]) {
      return availableLocales[localeHyphenUpperCase];
    } else if (availableLocales[localeHyphenLowerCase]) {
      return availableLocales[localeHyphenLowerCase];
    } else if (availableLocales[localeUnderscoreUpperCase]) {
      return availableLocales[localeUnderscoreUpperCase];
    } else if (availableLocales[localeUnderscoreLowerCase]) {
      return availableLocales[localeUnderscoreLowerCase];
    }
  }
}

/*
 *  The following don't have a place in the mapping table. They are stored with their country code in the state.
 *  PortugueseBrazil 'pt-BR'
 *  FrenchCanadian 'fr-CA'
 *  ChineseTraditional 'zh-CHT'
 */
const defaultLocalesMapping = {
  bg: 'bg-BG',
  da: 'da-DK',
  de: 'de-DE',
  el: 'el-GR',
  en: 'en-US',
  es: 'es-ES',
  fi: 'fi-FI',
  fr: 'fr-FR',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  nl: 'nl-NL',
  nb: 'nb-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  vi: 'vi-VN',
  zh: 'zh-CN'
};

export const defaultLanguage = defaultLocalesMapping.en;

/**
 * Get the full language code that is the closest match to the locale provided.
 * If a country code is provided with the locale, then the localeCode will be returned as is.
 * Or fall-back to default locale code for that language.
 * @param localeCode the code for the locale to look-up.
 * It can contain just language code or both language and country code.
 * @returns {*}
 */
export function getFullLanguageFromDefaultMapping(localeCode) {
  const [languageCode, countryCode] = localeCode.replace('_', '-').split('-');
  if (countryCode) {
    return localeCode.includes('-') ? `${languageCode}-${countryCode.toUpperCase()}` : localeCode;
  }

  const defaultLocale = defaultLocalesMapping[languageCode.toLowerCase()];
  return defaultLocale || defaultLanguage;
}

/**
 * Get the locale that is the closest match to the one provided. Will first check for an exact match, then fall-back to
 * default locale for that language.
 * @param localeCode the code for the locale to look-up
 * @param availableLocales an object where the keys are locale codes and the values are locales
 * @param defaultLocales override for the default locale codes for languages
 * @returns {*}
 */
export default function getMatchingLocale(localeCode, availableLocales, defaultLocales = defaultLocalesMapping) {
  const [languageCode, countryCode] = localeCode.replace('_', '-').split('-');
  let availableLocale;
  if (countryCode) {
    availableLocale = getAvailableLocale(languageCode, countryCode, availableLocales);
    if (availableLocale) {
      return availableLocale;
    }
  }

  availableLocale = getAvailableLocale(languageCode, undefined, availableLocales);
  if (availableLocale) {
    return availableLocale;
  }


  const defaultLocale = defaultLocales[languageCode];
  if (defaultLocale) {
    const [defaultLanguageCode, defaultCountryCode] = defaultLocale.split('-');
    availableLocale = getAvailableLocale(defaultLanguageCode, defaultCountryCode, availableLocales);
    if (availableLocale) {
      return availableLocale;
    }
  }
  throw new Error(`no available locale matching ${localeCode}`);
}

/**
 * Appends the locale parameter to the url
 * @param locale the combination of cultureCode with countryCode. Example: en-US
 * @param path the url link
 */
export const addLocaleToPath = (locale, path) => {
  return (locale) ? `${path}&locale=${locale}` : path;
};
